/**
 * OriginWarehousesPage — international company organizer manages
 * the origin warehouses they operate (Guangzhou, Istanbul, Yiwu, etc.).
 *
 *   GET  /api/cargo/origin-warehouses               — list (platform + mine)
 *   POST /api/cargo/origin-warehouses               — propose new (admin approves)
 *
 * Organizer must hold `container_organizer` capability + be KYC-verified
 * to propose. Approved warehouses become selectable in CreateContainerPage.
 *
 * The form only collects what the server's createOriginWarehouseSchema
 * needs (Joi-validated): country, city, name, addressLatin (mandatory) +
 * optional contact info / instructions file / shipment code.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Warehouse, Plus, Loader, Globe, Phone, MapPin,
  CheckCircle2, AlertCircle, FileText,
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { containersApi } from '@/api/containers';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import { FileUploadField } from '@/components/common/FileUploadField';
import { CONTAINER_COUNTRIES } from '@/constants/countries';
import type { OriginWarehouse } from '@/api/containers';

const FIELD_CLASSES =
  'w-full rounded-xl border border-border bg-surface px-4 py-3 text-text-primary outline-none transition focus:border-primary';

interface CreateForm {
  country: string;
  city: string;
  name: string;
  addressLatin: string;
  addressLocal?: string;
  contactName?: string;
  contactPhone?: string;
  shipmentCode?: string;
  instructionsFileUrl?: string;
  instructionsFileName?: string;
}

export default function OriginWarehousesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: warehouses, isLoading, error } = useQuery({
    queryKey: ['origin-warehouses'],
    queryFn: () => containersApi.listMyOriginWarehouses(),
  });

  const create = useMutation({
    mutationFn: (input: CreateForm) => containersApi.createOriginWarehouse(input),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['origin-warehouses'] }); },
  });

  const [showForm, setShowForm] = useState(false);

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
              <Warehouse size={20} /> {t('origin_warehouses_title', 'مستودعات المنشأ')}
            </h1>
            <p className="text-text-secondary text-sm">
              {t('origin_warehouses_subtitle', 'مستودعات تستلم بضائع المرسلين قبل الإقلاع. اقتراح المستودع يحتاج موافقة الإدارة.')}
            </p>
          </div>
          {!showForm && (
            <Button variant="primary" onClick={() => setShowForm(true)}>
              <Plus size={18} className="ml-1" />
              {t('propose_warehouse', 'اقتراح مستودع')}
            </Button>
          )}
        </div>

        {showForm && (
          <WarehouseForm
            loading={create.isPending}
            onCancel={() => setShowForm(false)}
            onSubmit={async (values) => {
              await create.mutateAsync(values);
              setShowForm(false);
            }}
            error={create.error ? (create.error as Error).message : null}
          />
        )}

        {isLoading ? (
          <div className="flex justify-center py-12 text-text-tertiary">
            <Loader size={28} className="animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-error/10 text-error rounded-xl p-4 text-sm">
            {t('list_load_failed', 'تعذّر تحميل المستودعات.')}
          </div>
        ) : !warehouses || warehouses.length === 0 ? (
          !showForm && (
            <div className="bg-surface border border-border rounded-2xl p-10 text-center">
              <Warehouse size={48} className="mx-auto text-text-tertiary mb-3" />
              <p className="text-text-secondary mb-2">
                {t('no_warehouses', 'لم تقترح أيّ مستودع بعد.')}
              </p>
              <p className="text-xs text-text-tertiary">
                {t('no_warehouses_hint', 'بعد الموافقة، تستطيع ربط أيّ حاوية تنشئها بهذا المستودع.')}
              </p>
            </div>
          )
        ) : (
          <div className="space-y-3 mt-3">
            {warehouses.map((w) => <WarehouseCard key={w.id} wh={w} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function WarehouseCard({ wh }: { wh: OriginWarehouse }) {
  const { t } = useTranslation();
  const country = CONTAINER_COUNTRIES.find((c) => c.code === wh.country.toLowerCase());
  const isApproved = wh.status === 'approved' || wh.is_active === true;

  return (
    <div className="bg-surface border border-border rounded-2xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Warehouse size={14} className="text-primary" />
            <h3 className="font-bold text-text-primary truncate">{wh.name}</h3>
          </div>
          <div className="flex items-center gap-1 text-xs text-text-secondary">
            <Globe size={11} />
            <span>{country?.name || wh.country.toUpperCase()}</span>
            <span className="text-text-tertiary">·</span>
            <span>{wh.city}</span>
          </div>
        </div>
        {isApproved ? (
          <span className="text-xs px-2 py-1 rounded-full bg-success/10 text-success font-bold flex items-center gap-1">
            <CheckCircle2 size={11} /> {t('approved', 'معتمَد')}
          </span>
        ) : (
          <span className="text-xs px-2 py-1 rounded-full bg-warning/10 text-warning font-bold flex items-center gap-1">
            <AlertCircle size={11} /> {t('pending', 'قيد المراجعة')}
          </span>
        )}
      </div>

      <div className="flex items-start gap-1 text-sm text-text-secondary mt-2">
        <MapPin size={12} className="mt-0.5 flex-shrink-0" />
        <span>{wh.address_latin}</span>
      </div>
      {wh.address_local && (
        <div className="text-sm text-text-secondary mt-1 pr-4">{wh.address_local}</div>
      )}
      {wh.contact_phone && (
        <div className="flex items-center gap-1 text-xs text-text-secondary mt-2">
          <Phone size={11} />
          <span dir="ltr">{wh.contact_phone}</span>
          {wh.contact_name && <span className="ml-2 text-text-tertiary">({wh.contact_name})</span>}
        </div>
      )}
      {wh.instructions_file_url && (
        <a
          href={wh.instructions_file_url}
          target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary hover:underline mt-2"
        >
          <FileText size={11} /> {wh.instructions_file_name || t('instructions_file', 'دليل المستودع')}
        </a>
      )}
      {wh.shipment_code && (
        <div className="text-xs text-text-tertiary mt-1">
          {t('shipment_code', 'رمز الشحنة')}: <code dir="ltr">{wh.shipment_code}</code>
        </div>
      )}
    </div>
  );
}

function WarehouseForm({
  loading, error, onSubmit, onCancel,
}: {
  loading: boolean;
  error: string | null;
  onSubmit: (v: CreateForm) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation();
  const [v, setV] = useState<CreateForm>({
    country: 'cn',
    city: '',
    name: '',
    addressLatin: '',
  });
  const [localError, setLocalError] = useState<string | null>(null);

  return (
    <form
      className="bg-surface border border-border rounded-2xl p-5 mb-4 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        setLocalError(null);
        if (v.name.trim().length < 3) return setLocalError(t('error_name_short', 'الاسم قصير (3 أحرف على الأقل)'));
        if (v.city.trim().length < 1) return setLocalError(t('error_city_required', 'المدينة إلزامية'));
        if (v.addressLatin.trim().length < 5) return setLocalError(t('error_address_short', 'العنوان قصير (5 أحرف على الأقل)'));
        onSubmit(v);
      }}
    >
      <h3 className="font-bold text-text-primary mb-2">
        {t('propose_warehouse_title', 'اقتراح مستودع جديد')}
      </h3>
      <p className="text-xs text-text-tertiary mb-3">
        {t('propose_warehouse_hint', 'الإدارة تراجع كل اقتراح. بعد الموافقة، يمكنك ربط الحاويات به.')}
      </p>

      {(error || localError) && (
        <div className="bg-error/10 text-error rounded-xl p-3 text-sm flex items-center gap-2">
          <AlertCircle size={16} /> {localError || error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Field label={t('country', 'البلد') + ' *'}>
          <select
            value={v.country}
            onChange={(e) => setV({ ...v, country: e.target.value })}
            className={FIELD_CLASSES}
            required
          >
            {CONTAINER_COUNTRIES.map((c) => (
              <option key={c.code} value={c.code}>{c.name}</option>
            ))}
          </select>
        </Field>
        <TextField
          label={t('city', 'المدينة') + ' *'}
          value={v.city}
          onChange={(e) => setV({ ...v, city: e.target.value })}
          required
          maxLength={128}
          placeholder={t('city_ph_warehouse', 'مثلاً: Guangzhou / Yiwu / Istanbul')}
        />
      </div>

      <TextField
        label={t('warehouse_name', 'اسم المستودع') + ' *'}
        value={v.name}
        onChange={(e) => setV({ ...v, name: e.target.value })}
        required
        minLength={3} maxLength={200}
        placeholder={t('warehouse_name_ph', 'مثال: Dragon Gate Yiwu Warehouse')}
      />

      <TextField
        label={t('address_latin', 'العنوان (إنجليزي/Latin)') + ' *'}
        value={v.addressLatin}
        onChange={(e) => setV({ ...v, addressLatin: e.target.value })}
        required minLength={5} maxLength={500}
        placeholder="123 Main St, District, City, Postal Code"
        dir="ltr"
      />

      <Field label={t('address_local', 'العنوان (محلي — اختياري)')}>
        <textarea
          value={v.addressLocal || ''}
          onChange={(e) => setV({ ...v, addressLocal: e.target.value })}
          className={FIELD_CLASSES + ' min-h-[60px]'}
          maxLength={500}
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <TextField
          label={t('contact_name', 'اسم المسؤول')}
          value={v.contactName || ''}
          onChange={(e) => setV({ ...v, contactName: e.target.value })}
          maxLength={128}
        />
        <TextField
          label={t('contact_phone', 'هاتف المسؤول')}
          value={v.contactPhone || ''}
          onChange={(e) => setV({ ...v, contactPhone: e.target.value })}
          dir="ltr" maxLength={32}
          placeholder="+86..."
        />
      </div>

      <TextField
        label={t('shipment_code', 'رمز الشحنة (2-6 حروف)')}
        value={v.shipmentCode || ''}
        onChange={(e) => setV({ ...v, shipmentCode: e.target.value.toUpperCase() })}
        dir="ltr" maxLength={6}
        placeholder="DG / MAERSK / ..."
      />

      <Field label={t('instructions_file', 'دليل المستودع (PDF / Excel — اختياري)')}>
        <FileUploadField
          kind="instructions"
          currentUrl={v.instructionsFileUrl}
          currentFilename={v.instructionsFileName}
          onUploaded={(url, filename) => setV({ ...v, instructionsFileUrl: url, instructionsFileName: filename })}
          onClear={() => setV({ ...v, instructionsFileUrl: undefined, instructionsFileName: undefined })}
        />
      </Field>

      <div className="flex gap-2 pt-1">
        <Button variant="primary" loading={loading} type="submit">
          {t('submit_warehouse', 'إرسال للمراجعة')}
        </Button>
        <Button variant="ghost" type="button" onClick={onCancel}>
          {t('cancel', 'إلغاء')}
        </Button>
      </div>
    </form>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs text-text-secondary mb-1 block">{label}</span>
      {children}
    </label>
  );
}
